import { Fragment, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { useLocalStorage } from '~/hooks/components/use-storage';

type Props = {
  option: Queries.OptionValuesFragmentFragment;
  type: string;
  selectedOptions: Queries.VariantDetailsFragmentFragment['optionValues'];
  handleSelection: (option: Queries.OptionValuesFragmentFragment) => void;
  isKit: boolean;
  disabled?: boolean;
  productId: string;
  isOptionAvailable: boolean;
};

const ProductOptionItem = ({
  option,
  type,
  selectedOptions,
  handleSelection,
  isKit,
  isOptionAvailable,
  productId,
}: Props) => {
  const { setValue: setWomensSize } = useLocalStorage('womensSize');
  const { setValue: setMensSize } = useLocalStorage('mensSize');
  const { pathname } = useLocation();
  const [isSelected, setIsSelected] = useState(false);
  const isColor = type === 'color';

  useEffect(() => {
    const match = selectedOptions?.find((o) => o?.slug === option.slug);
    setIsSelected(Boolean(match));
  }, [selectedOptions, option.slug]);

  const onSelect = (option: Queries.OptionValuesFragmentFragment) => {
    if (option?.name?.includes('Size')) {
      const isWomens = pathname.split('/').some((path) => path.includes('womens'));
      const optionsArr = option?.presentation?.split('-');
      const isVest = optionsArr && optionsArr.length > 1;
      if (isWomens && isVest) {
        setWomensSize({ vest: option.presentation, apparel: optionsArr[0].split('-')[0] });
      }
      if (isWomens && !isVest) {
        setWomensSize({
          vest: VEST_SIZE_MAPPING['womens'][option?.presentation as keyof (typeof VEST_SIZE_MAPPING)['womens']],
          apparel: option.presentation,
        });
      }
      if (!isWomens && isVest) {
        setMensSize({
          vest: option.presentation,
          apparel: optionsArr[0].split('-')[0],
        });
      }
      if (!isWomens && !isVest) {
        setMensSize({
          vest: VEST_SIZE_MAPPING['mens'][option.presentation as keyof (typeof VEST_SIZE_MAPPING)['mens']],
          apparel: option.presentation,
        });
      }
    }

    handleSelection(option);
  };

  // this takes care of products with more than 4 options to choose from (g-vest) when shown in a kit
  const getStyleForKit = () =>
    isKit
      ? {
          '@media(max-width: 420px)': {
            ':not(:last-of-type)': {
              marginRight: type === 'color' ? '1rem' : '0.8rem',
            },
          },
        }
      : {};

  return (
    <Fragment>
      <input
        type="radio"
        name={type}
        value={option.slug ?? ''}
        checked={isSelected}
        data-element={type}
        onChange={({ target: { checked } }) => (checked ? onSelect(option) : null)}
        sx={{
          clipPath: 'inset(100%)',
          clip: 'rect(1px, 1px, 1px, 1px)',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          position: 'absolute',
          whiteSpace: 'nowrap',
        }}
        id={`${option.slug}-${productId}`}
      />
      <label
        htmlFor={`${option.slug}-${productId}`}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: isColor ? '2.4rem' : 'auto',
          height: isColor ? '2.4rem' : '2.8rem',
          padding: isColor ? 0 : '0.8rem 1rem',
          fontSize: ['1.5rem', '1.7rem'],
          fontWeight: isSelected ? 500 : 400,
          lineHeight: 0.9,
          backgroundColor: isColor ? option.alternateRendering : 'transparent',
          borderRadius: isColor ? '50%' : '4px',
          border: isColor ? null : '1px solid',
          borderColor: isSelected ? 'black' : 'cloud',
          outline: isColor && isSelected ? '1px solid black' : null,
          outlineOffset: '2px',
          cursor: 'pointer',

          ...(!isOptionAvailable && !isColor
            ? {
                '::after': {
                  content: '""',
                  position: 'absolute',
                  left: '-2px',
                  right: '-2px',
                  top: '50%',
                  height: '1px',
                  backgroundColor: 'UIGray1',
                  transform: 'rotate(-45deg) translateY(-50%)',
                  transformOrigin: 'center',
                  pointerEvents: 'none',
                },
                '::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'cloud',
                  opacity: 0.5,
                },
              }
            : {}),

          ...(isColor && option.alternateRendering2
            ? {
                '::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: option.alternateRendering2,
                  clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)',
                },
              }
            : {}),
          ...getStyleForKit(),
        }}
        title={`${option.title}`}
        aria-label={`${option.title}`}
      >
        {(type === 'size' || type === 'amount') && option.presentation}
      </label>
    </Fragment>
  );
};

export default ProductOptionItem;

const VEST_SIZE_MAPPING = {
  mens: {
    S: 'S - M',
    M: 'S - M',
    L: 'L - XL',
    XL: 'L - XL',
  },
  womens: {
    XS: 'S - M',
    S: 'S - M',
    M: 'S - M',
    L: 'L - XL',
    XL: 'L - XL',
  },
};
